import { useTreeDiff } from '@/composables/useTreeDiff';
import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { cameraControls } from '@/threejs/core';
import eventBus from '@/utils/eventBus';
import CameraControls from 'camera-controls';
import { watch } from 'vue';

export const useProjectSettings = () => {
  const cruxModuleStore = useCruxModuleStore();
  const { diffSettings } = useTreeDiff();

  // Helper function to get current project settings
  const getCurrentProjectSettings = () => {
    const projectModule = cruxModuleStore.cruxModules.find((module) => module.type === 'project_settings');
    return projectModule ? projectModule.data : {};
  };

  const stopHistoryWatch = watch(
    () => cruxModuleStore.history,
    ([newSnapshot], [oldSnapshot]) => {
      const oldProjectModule = oldSnapshot.snapshot?.find((m) => m.type === 'project_settings');
      const newProjectModule = newSnapshot.snapshot?.find((m) => m.type === 'project_settings');

      handleViewChanges(oldProjectModule?.data, newProjectModule?.data);
    }
  );

  const handleLengthUnitsChange = (value) => {
    // Emit event for components that need to update based on unit change
    eventBus.emit('units:length:changed', value);
    // Update any measurements or displays that show length units
    updateMeasurementDisplays('length', value);
  };

  const handleAngleUnitsChange = (value) => {
    // Emit event for components that need to update based on angle unit change
    eventBus.emit('units:angle:changed', value);

    // Update any displays that show angles
    updateMeasurementDisplays('angle', value);
  };

  const handleTemperatureUnitsChange = (value) => {
    // Emit event for temperature unit changes
    eventBus.emit('units:temperature:changed', value);

    // Update any displays that show temperature
    updateMeasurementDisplays('temperature', value);
  };

  const handlePhotometricUnitsChange = (value) => {
    // Emit event for photometric unit changes
    eventBus.emit('units:photometric:changed', value);

    // Update any photometric calculations and displays
    updatePhotometricDisplays(value);
  };

  const handleMouseControlsChange = (value) => {
    // Update camera controls based on mouse control settings
    if (value === 'rotate') {
      cameraControls.mouseButtons.left = CameraControls.ACTION.ROTATE;
      cameraControls.mouseButtons.right = CameraControls.ACTION.TRUCK;
      cameraControls.touches.one = CameraControls.ACTION.TOUCH_ROTATE;
      cameraControls.touches.two = CameraControls.ACTION.TOUCH_DOLLY_TRUCK;
    } else {
      cameraControls.mouseButtons.left = CameraControls.ACTION.TRUCK;
      cameraControls.mouseButtons.right = CameraControls.ACTION.ROTATE;
      cameraControls.touches.one = CameraControls.ACTION.TOUCH_TRUCK;
      cameraControls.touches.two = CameraControls.ACTION.TOUCH_DOLLY_ROTATE;
    }
  };

  const handleCoordinateSystemChange = (value) => {
    // Emit event for coordinate system changes
    if (value === 'left-handed') {
      //scene.scale.set(1, 1, 1)
    } else {
      //scene.scale.set(-1, 1, 1)
    }
  };

  const updateMeasurementDisplays = (unitType, unitValue) => {
    // This function would update any measurement displays in the scene
    // For example, measurement tools, dimension lines, etc.

    // Emit a more specific event that measurement components can listen to
    eventBus.emit('measurements:units:updated', {
      type: unitType,
      value: unitValue,
    });
  };

  const updatePhotometricDisplays = (unitSystem) => {
    // Update photometric calculations and displays
    // This might affect how light values are displayed in projectors,
    // light fixtures, and photometric analysis tools

    eventBus.emit('photometric:units:updated', unitSystem);

    // If there are any photometric overlays or displays in the scene,
    // they should be updated here
  };

  const handleViewChanges = (oldSnapshot, newSnapshot) => {
    const changes = diffSettings(oldSnapshot, newSnapshot);
    if (changes.modified.length) {
      changes.modified.forEach((change) => {
        handleProjectSettingChange(change.key, change.value);
      });
      // Note: Project settings typically don't require re-rendering the 3D scene
      // but we might need to update UI components
    }
  };

  const handleProjectSettingChange = (key, value) => {
    switch (key) {
      // Units settings
      case 'units.length':
        handleLengthUnitsChange(value);
        break;
      case 'units.angle':
        handleAngleUnitsChange(value);
        break;
      case 'units.temperature':
        handleTemperatureUnitsChange(value);
        break;
      case 'units.photometric':
        handlePhotometricUnitsChange(value);
        break;

      case 'mouse.controls':
        handleMouseControlsChange(value);
        break;
      case 'coordinateSystem':
        handleCoordinateSystemChange(value);
        break;
      // Add other project-level settings as needed
      default:
        console.warn(`Unhandled project setting change: ${key} = ${value}`);
        break;
    }
  };

  // Utility functions to convert between units
  const convertLength = (value, fromUnit, toUnit) => {
    const conversions = {
      meters: 1,
      centimeters: 100,
      millimeters: 1000,
      feet: 3.28084,
      inches: 39.3701,
    };

    const metersValue = value / conversions[fromUnit];
    return metersValue * conversions[toUnit];
  };

  const convertAngle = (value, fromUnit, toUnit) => {
    if (fromUnit === toUnit) return value;

    if (fromUnit === 'degrees' && toUnit === 'radians') {
      return value * (Math.PI / 180);
    }

    if (fromUnit === 'radians' && toUnit === 'degrees') {
      return value * (180 / Math.PI);
    }

    return value;
  };

  const convertTemperature = (value, fromUnit, toUnit) => {
    if (fromUnit === toUnit) return value;

    // Convert to Kelvin first, then to target unit
    let kelvin = value;

    if (fromUnit === 'celsius') {
      kelvin = value + 273.15;
    } else if (fromUnit === 'fahrenheit') {
      kelvin = (value - 32) * (5 / 9) + 273.15;
    }

    if (toUnit === 'celsius') {
      return kelvin - 273.15;
    } else if (toUnit === 'fahrenheit') {
      return (kelvin - 273.15) * (9 / 5) + 32;
    }

    return kelvin;
  };

  return {
    handleProjectSettingChange,
    convertLength,
    convertAngle,
    convertTemperature,
    stopHistoryWatch,
  };
};
